/* aurora-quick-buy.js — quick view in a right-side DRAWER (matches cart/search drawers).

   The modal shell (snippets/aurora-quick-buy-drawer.liquid) ships empty on every page. The
   first time a [data-aurora-quickbuy-open] trigger is clicked we fetch that product with
   ?view=aurora-quick-buy — templates/product.aurora-quick-buy.json, whose main section is our
   real "Product information" section — and inject it. So the quick view offers exactly the same
   blocks as the product page, stays merchant-editable in the theme editor, and costs nothing on
   pages where it is never opened.

   The panel slides via an .is-shown class added one frame after .is-open (see the CSS note).
   The section's CSS already ships in the global aurora-sections.css bundle; its JS
   (product-info / product-form / aurora-main-product) is loaded on first open only. */
(function () {
  var VIEW = 'aurora-quick-buy';
  var loaded = {};

  /* load a script once; resolve on error too, so one bad file can't hang the modal */
  function script(src) {
    if (loaded[src]) return loaded[src];
    loaded[src] = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = s.onerror = function () { resolve(); };
      document.head.appendChild(s);
    });
    return loaded[src];
  }

  /* the fetched section declares what it needs — mirror its <script src> tags instead of
     hardcoding a list that would rot whenever the product section changes */
  function deps(section) {
    var srcs = [];
    section.querySelectorAll('script[src]').forEach(function (s) {
      srcs.push(s.getAttribute('src'));
      s.remove(); // imported <script> nodes don't execute reliably; we load them ourselves
    });
    return Promise.all(srcs.map(script));
  }

  /* Prefetch cache: url -> Promise<html>. The panel itself opens instantly; what used
     to lag was the full-page fetch + parse firing only on click. We now start that work
     on hover / focus / touchstart, so by the time the click lands the HTML is usually
     already in flight or done. Reopening a product is then instant. */
  var CACHE = Object.create(null);

  function grab(url) {
    if (CACHE[url]) return CACHE[url];
    var req = fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'view=' + VIEW)
      .then(function (r) {
        if (!r.ok) throw new Error('quick view: ' + r.status);
        return r.text();
      })
      .catch(function (e) { delete CACHE[url]; throw e; }); // don't cache failures
    CACHE[url] = req;
    return req;
  }

  class AuroraQuickBuy extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';
      var self = this;
      /* The shell is rendered inside header.liquid, and the header section group is
         `position: sticky; z-index: 3`, which creates a stacking context. That trapped the
         whole drawer at an effective z-index of 3, so product-card wishlist hearts (also
         z-index 3, and later in the DOM) painted OVER the drawer. Lifting to <body> escapes
         that context — the cart / search / wishlist drawers already do exactly this. */
      if (this.parentElement !== document.body) document.body.appendChild(this);

      this.content = this.querySelector('[data-aqb-content]');
      this.foot = this.querySelector('[data-aqb-foot]');
      this.full = this.querySelector('[data-aqb-full]');
      if (!this.content) return;

      document.addEventListener('click', function (e) {
        var t = e.target.closest('[data-aurora-quickbuy-open]');
        if (t) {
          e.preventDefault();
          /* Nothing to choose -> skip the drawer entirely: add the one variant and open the
             cart drawer. data-quickbuy-direct is only emitted for a single-variant product
             that is in stock, so a sold-out one still gets the drawer instead of a failed add. */
          var direct = t.getAttribute('data-quickbuy-direct');
          if (direct) return self.addDirect(direct, t);
          return self.open(t.getAttribute('data-product-url'));
        }
        if (e.target.closest('[data-aqb-close]')) return self.close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.classList.contains('is-open')) self.close();
      });

      // warm the cache the moment the shopper shows intent — pointerenter covers mouse
      // hover, touchstart covers phones (fires well before the click), focusin covers keyboard
      ['pointerenter', 'touchstart', 'focusin'].forEach(function (evt) {
        document.addEventListener(evt, function (e) {
          var t = e.target.closest && e.target.closest('[data-aurora-quickbuy-open]');
          if (!t) return;
          var u = t.getAttribute('data-product-url');
          if (u) grab(u).catch(function () {});   // silent: the click path handles errors
        }, { capture: true, passive: true });
      });
      // an add-to-cart opens the cart drawer over us — step aside
      if (window.PUB_SUB_EVENTS && window.subscribe) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { self.close(); });
      }
    }

    /* Add straight to cart, then hand off to the cart drawer. Uses the same routes and the
       same cartUpdate broadcast the rest of the theme listens to, so the header count, the
       drawer contents and any free-shipping bar all refresh themselves. */
    addDirect(variantId, trigger) {
      var self = this;
      if (trigger) { trigger.classList.add('is-loading'); trigger.disabled = true; }
      var done = function () { if (trigger) { trigger.classList.remove('is-loading'); trigger.disabled = false; } };

      var sections = [];
      var drawer = document.querySelector('cart-drawer, #CartDrawer, [data-cart-drawer]');
      if (drawer && drawer.dataset && drawer.dataset.sectionId) sections.push(drawer.dataset.sectionId);

      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('add ' + r.status); return r.json(); })
        .then(function () {
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'aurora-quick-buy' });
          }
          // open the cart drawer however this theme exposes it
          var d = document.querySelector('cart-drawer');
          if (d && typeof d.open === 'function') d.open();
          else {
            var opener = document.querySelector('#cart-icon-bubble, [data-cart-drawer-open]');
            if (opener) opener.click();
          }
        })
        .catch(function () {
          // never strand the shopper — fall back to the drawer so they can still buy
          if (trigger) self.open(trigger.getAttribute('data-product-url'));
        })
        .then(done, done);
    }

    open(url) {
      if (!url) return;
      this.classList.add('is-open');
      // slide in — see the .is-shown note in aurora-quick-buy.css. Reading offsetWidth forces a
      // synchronous reflow so the panel is laid out at translateX(100%) WHILE visible before
      // .is-shown flips the transform, so the transition runs (rAF is unreliable — it throttles
      // in background tabs; a forced reflow always works).
      void this.offsetWidth;
      this.classList.add('is-shown');
      document.body.classList.add('aqb-locked'); // lock: see the html:has() rules in aurora.css
      if (this.full) this.full.href = url;

      if (this.__url === url) return; // already showing this product
      this.__url = url;
      this.classList.add('is-loading');

      var self = this;
      grab(url)
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var info0 = doc.querySelector('product-info');
          var section = info0 && (info0.closest('.shopify-section') || info0);
          if (!section) throw new Error('quick view: no section in response');
          return deps(section).then(function () {
            var node = document.importNode(section, true);
            var info = node.matches('product-info') ? node : node.querySelector('product-info');
            if (info) {
              // the section is built for a real page: left as-is, picking a variant here would
              // rewrite the address bar (and full-page-swap) over the page you're browsing
              info.dataset.updateUrl = 'false';
              info.id = info.id + '-qv';
            }
            self.content.innerHTML = '';
            self.content.appendChild(node);
            /* NOTE: do NOT relocate the buy-buttons block out of the section. It contains the
               <product-form> and its hidden input[name="id"], and product-info.js updates that
               input by querying WITHIN the section — moving it silently broke variant
               switching, so the cart received the first variant no matter what was picked.
               The bar is pinned with `position: sticky` instead (see the CSS). */
            self.classList.remove('is-loading');
          });
        })
        .catch(function () {
          self.__url = null;
          window.location = url; // never strand the shopper — fall back to the product page
        });
    }

    close() {
      this.classList.remove('is-shown'); // slide out first (transform transition)
      this.classList.remove('is-open');
      document.body.classList.remove('aqb-locked');
    }
  }

  if (!customElements.get('aurora-quick-buy')) customElements.define('aurora-quick-buy', AuroraQuickBuy);
})();
